import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppFloat from "./components/WhatsAppFloat";
import CanonicalTag from "./components/CanonicalTag";
import AnalyticsManager from "./components/AnalyticsManager";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import GoAMLServices from "./pages/GoAMLServices";
import ValuationServices from "./pages/ValuationServices";
import IndustryExpertise from "./pages/IndustryExpertise";
import WhyChooseUs from "./pages/WhyChooseUs";
import Team from "./pages/Team";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import FraudAlert from "./pages/FraudAlert";
import Careers from "./pages/Careers";
import CorporateTaxUAE from "./pages/CorporateTaxUAE";
import VATServicesUAE from "./pages/VATServicesUAE";
import EInvoicingUAE from "./pages/EInvoicingUAE";
import AuditServicesUAE from "./pages/AuditServicesUAE";
import AccountingServicesUAE from "./pages/AccountingServicesUAE";
import CompanyFormationUAE from "./pages/CompanyFormationUAE";
import BusinessAdvisoryUAE from "./pages/BusinessAdvisoryUAE";
import SMEBusinessServicesUAE from "./pages/SMEBusinessServicesUAE";
import FreeZoneServicesUAE from "./pages/FreeZoneServicesUAE";
import HamriyahFreeZoneServices from "./pages/HamriyahFreeZoneServices";
import SAIFZoneServices from "./pages/SAIFZoneServices";
import FreeZoneAuthorityPage from "./pages/FreeZoneAuthorityPage";
import { freeZones } from "./data/freeZones";
import KnowledgeAcademy from "./pages/KnowledgeAcademy";
import KnowledgeAcademyArticle from "./pages/KnowledgeAcademyArticle";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import NotFound from "./pages/NotFound";

// Marketing chrome (header/footer/WhatsApp button) is hidden on admin routes
// so the dashboard reads as a distinct product surface.
function ChromeLayout({ children }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) return children;
  return (
    <>
      <Header />
      <main id="main-content" role="main">{children}</main>
      <Footer />
      <WhatsAppFloat />
    </>
  );
}

// Earlier sibling of the routes, so pages that scroll to a section on mount still win.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export function AppRoutes() {
  return (
        <AuthProvider>
          <ScrollToTop />
          <CanonicalTag />
          <AnalyticsManager />
          <ChromeLayout>
            <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/goaml-compliance" element={<GoAMLServices />} />
          <Route path="/services/valuation" element={<ValuationServices />} />
          <Route path="/industries" element={<IndustryExpertise />} />
          <Route path="/why-choose-us" element={<WhyChooseUs />} />
          <Route path="/team" element={<Team />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<BlogPost />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/fraud-alert" element={<FraudAlert />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/corporate-tax-uae" element={<CorporateTaxUAE />} />
          <Route path="/vat-services-uae" element={<VATServicesUAE />} />
          <Route path="/e-invoicing-uae" element={<EInvoicingUAE />} />
          <Route path="/audit-services-uae" element={<AuditServicesUAE />} />
          <Route path="/accounting-services-uae" element={<AccountingServicesUAE />} />
          <Route path="/company-formation-uae" element={<CompanyFormationUAE />} />
          <Route path="/business-advisory-uae" element={<BusinessAdvisoryUAE />} />
          <Route path="/sme-business-services-uae" element={<SMEBusinessServicesUAE />} />
          <Route path="/free-zone-services-uae" element={<FreeZoneServicesUAE />} />
          <Route path="/hamriyah-free-zone-services" element={<HamriyahFreeZoneServices />} />
          <Route path="/saif-zone-services" element={<SAIFZoneServices />} />
          <Route path="/dmcc-audit-services" element={<FreeZoneAuthorityPage zone={freeZones.dmcc} />} />
          <Route path="/dafza-services" element={<FreeZoneAuthorityPage zone={freeZones.dafza} />} />
          <Route path="/ajman-free-zone-services" element={<FreeZoneAuthorityPage zone={freeZones.ajman} />} />
          <Route path="/shams-services" element={<FreeZoneAuthorityPage zone={freeZones.shams} />} />
          <Route path="/spc-free-zone-services" element={<FreeZoneAuthorityPage zone={freeZones.spc} />} />
          <Route path="/dubai-internet-city-services" element={<FreeZoneAuthorityPage zone={freeZones['dubai-internet-city']} />} />
          <Route path="/rakez-services" element={<FreeZoneAuthorityPage zone={freeZones.rakez} />} />
          <Route path="/ifza-services" element={<FreeZoneAuthorityPage zone={freeZones.ifza} />} />
          <Route path="/knowledge-academy" element={<KnowledgeAcademy />} />
          <Route path="/knowledge-academy/:slug" element={<KnowledgeAcademyArticle />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </ChromeLayout>
        <Toaster />
        </AuthProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
